// Radha — auth via Supabase, the template's identity layer.
//
// Replaces the bespoke RS256 login (server/services/gateway) with Supabase Auth.
// signInWithPassword returns a session whose token RLS reads directly; there is
// no gateway to mint or verify anything. The tenant is NOT in the token — it is
// resolved server-side from the user's email by auth_tenant_id() (migration
// 017), so the client never asserts, and cannot forge, its tenancy.
import { createContext, useContext, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";

import { supabase } from "./supabase";

// Role + write-permission for the current user, provided by the shell and read by
// panels to gate write controls. auditor is read-only (compliance viewer).
export const RoleContext = createContext<{ role: string | null; canWrite: boolean }>({
  role: null,
  canWrite: true,
});
export const useRole = () => useContext(RoleContext);
export const canWriteFor = (role: string | null | undefined) => role !== "auditor";

export function useSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  return { session, loading };
}

export async function signIn(email: string, password: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}

export interface Me {
  full_name: string;
  role: string;
  tenant_legal_name: string | null;
  tenant_gstin: string | null;
  tenant_upi_vpa: string | null;
  early_pay_discount_pct: number;
  early_pay_discount_days: number;
  po_portal_auto_threshold: number;
  direct_pay_max_threshold: number;
  tenant_is_msme: boolean;
  // Jurisdiction (063). Drives money/date formatting client-side and the
  // collection-conduct window server-side.
  tenant_country: string;
  tenant_base_currency: string;
  tenant_timezone: string;
  tenant_locale: string;
}

/**
 * The signed-in user's display identity, from the auth_me() RPC (migration 026).
 * public.users is not directly readable on the PostgREST path, so this is the
 * only channel for it — and it returns solely the caller's own two fields.
 */
export function useMe(enabled: boolean) {
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    if (!enabled) {
      setMe(null);
      return;
    }
    let cancelled = false;
    supabase
      .rpc("auth_me")
      .then(({ data }) => {
        if (!cancelled) setMe((data as Me[] | null)?.[0] ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return me;
}
